import { useGetAllFeatureSwitches, useGetSystemFlowSwitch } from '@src/api/config-api'
import { useContext, useMemo, type FC, type ReactNode } from 'react'
import { AppConfigContext, AppConfigContextValue } from '../contexts/app-config-context'

interface AppConfigProviderProps {
  children: ReactNode
}

export const AppConfigProvider: FC<AppConfigProviderProps> = ({ children }) => {
  const [featureSwitches, isFetchingFeatureSwitches] = useGetAllFeatureSwitches()
  const [systemSwitches, isFetchingSystemSwitches] = useGetSystemFlowSwitch()
  // Calculate overall loading state
  const isInitializing = isFetchingFeatureSwitches || isFetchingSystemSwitches
  // Build context value
  const contextValue = useMemo<AppConfigContextValue>(
    () => ({
      isInitializing,
      featureSwitchInfo: featureSwitches ?? null,
      systemFlowInfo: systemSwitches ?? null,
    }),
    [featureSwitches, systemSwitches, isInitializing],
  )

  return <AppConfigContext.Provider value={contextValue}>{children}</AppConfigContext.Provider>
}

/**
 * Hook to check if app config is still loading
 * Use this in App.tsx to show loading state
 */
export function useIsAppConfigLoading(): boolean {
  const context = useContext(AppConfigContext)
  return context?.isInitializing ?? true
}

// Re-export context hook for convenience
export { useAppConfigContext } from '../contexts/app-config-context'
